import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ALL_BUSINESS_MODULE_KEYS, DEFAULT_FREE_BUSINESS_MODULES, hasModuleAccess, mergeLegacyFreeModules } from '../constants/business-modules.catalog';

export interface PlanModulesContext {
  maxModules: number | null;
  availableModules: string[];
  enabledModules: string[];
  modulesRemaining: number;
  userConfigured: boolean;
}

@Injectable({ providedIn: 'root' })
export class PlanModulesService {
  private modulesSubject: BehaviorSubject<string[]>;
  private contextSubject = new BehaviorSubject<PlanModulesContext>({
    maxModules: null,
    availableModules: [],
    enabledModules: [],
    modulesRemaining: 0,
    userConfigured: false,
  });

  readonly modules$;
  readonly context$;

  constructor() {
    const merged = this.loadFromStorage();
    this.modulesSubject = new BehaviorSubject<string[]>(merged);
    this.modules$ = this.modulesSubject.asObservable();
    this.context$ = this.contextSubject.asObservable();
    this.persistModules(merged);
    this.loadContextFromStorage();
  }

  get modules(): string[] {
    return this.modulesSubject.value;
  }

  get context(): PlanModulesContext {
    return this.contextSubject.value;
  }

  private persistModules(normalized: string[]): void {
    try {
      const raw = localStorage.getItem('currentUser');
      if (raw) {
        const user = JSON.parse(raw);
        user.planModules = normalized;
        localStorage.setItem('currentUser', JSON.stringify(user));
      }
    } catch { /* ignore */ }
  }

  private persistContext(context: Partial<PlanModulesContext>): void {
    try {
      const raw = localStorage.getItem('currentUser');
      if (raw) {
        const user = JSON.parse(raw);
        if (context.maxModules !== undefined) user.maxModules = context.maxModules;
        if (context.availableModules !== undefined) user.availableModules = context.availableModules;
        if (context.enabledModules !== undefined) user.enabledModules = context.enabledModules;
        if (context.modulesRemaining !== undefined) user.modulesRemaining = context.modulesRemaining;
        if (context.userConfigured !== undefined) user.userConfigured = context.userConfigured;
        localStorage.setItem('currentUser', JSON.stringify(user));
      }
    } catch { /* ignore */ }
  }

  private loadContextFromStorage(): void {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) return;
      const user = JSON.parse(raw);
      this.contextSubject.next({
        maxModules: user.maxModules ?? null,
        availableModules: Array.isArray(user.availableModules) ? user.availableModules : [],
        enabledModules: Array.isArray(user.enabledModules) ? user.enabledModules : [],
        modulesRemaining: user.modulesRemaining ?? 0,
        userConfigured: !!user.userConfigured,
      });
    } catch { /* ignore */ }
  }

  private normalizeModules(modules: string[] | null | undefined): string[] {
    const base = modules?.length ? [...modules] : [...ALL_BUSINESS_MODULE_KEYS];
    const valid = base.filter((k) => ALL_BUSINESS_MODULE_KEYS.includes(k));
    const normalized = valid.length > 0 ? valid : [...ALL_BUSINESS_MODULE_KEYS];
    return mergeLegacyFreeModules(normalized);
  }

  setModules(modules: string[] | null | undefined): void {
    const normalized = this.normalizeModules(modules);
    this.modulesSubject.next(normalized);
    this.persistModules(normalized);
  }

  applyAuthPayload(data: {
    planModules?: string[];
    maxModules?: number | null;
    availableModules?: string[];
    enabledModules?: string[];
    modulesRemaining?: number;
    userConfigured?: boolean;
  }): void {
    if (Array.isArray(data.planModules)) {
      this.setModules(data.planModules);
    }
    const context: PlanModulesContext = {
      maxModules: data.maxModules ?? null,
      availableModules: data.availableModules ?? [],
      enabledModules: data.enabledModules ?? [],
      modulesRemaining: data.modulesRemaining ?? 0,
      userConfigured: !!data.userConfigured,
    };
    this.contextSubject.next(context);
    this.persistContext(context);
  }

  applyModuleSelection(data: {
    effectiveModules: string[];
    maxModules?: number | null;
    availableModules?: string[];
    enabledModules?: string[];
    modulesRemaining?: number;
    userConfigured?: boolean;
  }): void {
    this.setModules(data.effectiveModules);
    const context: PlanModulesContext = {
      maxModules: data.maxModules ?? this.context.maxModules,
      availableModules: data.availableModules ?? this.context.availableModules,
      enabledModules: data.enabledModules ?? this.context.enabledModules,
      modulesRemaining: data.modulesRemaining ?? this.context.modulesRemaining,
      userConfigured: data.userConfigured ?? true,
    };
    this.contextSubject.next(context);
    this.persistContext(context);
  }

  loadFromStorage(): string[] {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) return mergeLegacyFreeModules([...DEFAULT_FREE_BUSINESS_MODULES]);
      const user = JSON.parse(raw);
      if (Array.isArray(user.planModules) && user.planModules.length > 0) {
        return this.normalizeModules(user.planModules);
      }
    } catch { /* ignore */ }
    return mergeLegacyFreeModules([...DEFAULT_FREE_BUSINESS_MODULES]);
  }

  hasModule(moduleKey: string | undefined): boolean {
    if (this.isBypassRole()) return true;
    return hasModuleAccess(this.modules, moduleKey);
  }

  isBypassRole(): boolean {
    try {
      const mode = localStorage.getItem('activeMode');
      if (mode && mode !== 'business') return true;
      const raw = localStorage.getItem('currentUser');
      if (!raw) return false;
      const user = JSON.parse(raw);
      return user.roles === 'admin';
    } catch {
      return false;
    }
  }

  isGroupVisible(groupKey: string, childKeys: string[]): boolean {
    if (this.isBypassRole()) return true;
    if (this.hasModule(groupKey)) return true;
    return childKeys.some((k) => this.hasModule(k));
  }
}
